import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/ui/brand-mark";
import { Section } from "../components/ui/section";
import { useAuth } from "../auth/AuthContext";
import { downloadBlob, makeZip, type ZipEntry } from "../lib/zip";
import {
  logoUrl,
  logomarkUrl,
  appIconUrl,
  brandKitAssets,
  screenshotDashboard,
  screenshotGroupDetail,
  screenshotGroups,
  screenshotExpenseDetail,
  screenshotProfile,
} from "../assets";

/**
 * Public brand / press page. A single self-contained reference for the
 * Tailspend identity — name, logo, colours, voice — plus copy-paste-ready
 * metadata and store-listing text for the App Store and Google Play. Renders
 * outside the authenticated AppLayout (like /about and /privacy) so it's
 * reachable signed-out and shareable.
 *
 * Values here mirror the live app: colours come from `@repo/design-tokens`
 * (theme.css) and the app metadata matches `apps/mobile/app.json` /
 * `eas.json`. Logo assets are bundled from `../assets` (see `assets/index.ts`).
 * The downloadable brand kit is assembled in the browser at click-time (see
 * `lib/zip`) — no zip is committed, so it never drifts from these values.
 */

const APP = {
  name: "Tailspend",
  tagline: "Splitting expenses, minus the spreadsheet",
  subtitle: "Track shared expenses with friends, roommates, and trips.",
  bundleId: "studio.kinesis.tailspend",
  iosAppId: "6777495715",
  category: "Finance",
  website: "https://tailspend.kinesis.studio",
  privacy: "https://tailspend.kinesis.studio/privacy",
  support: "support@tailspend.kinesis.studio",
} as const;

// Brand palette — the source values are in packages/design-tokens/theme.css.
const PALETTE: { name: string; hex: string; on?: string; note: string }[] = [
  {
    name: "Primary",
    hex: "#5300E7",
    on: "#FFFFFF",
    note: "Core brand purple — buttons, logomark, accents.",
  },
  {
    name: "Primary container",
    hex: "#6C38FF",
    on: "#E9E0FF",
    note: "Raised primary surfaces.",
  },
  {
    name: "Primary fixed",
    hex: "#E7DEFF",
    on: "#1E0060",
    note: "Soft tint behind the logomark / icon badges.",
  },
  {
    name: "Tertiary",
    hex: "#005A3C",
    on: "#FFFFFF",
    note: "Positive / 'you're owed' accent.",
  },
  {
    name: "Secondary container",
    hex: "#FE9800",
    on: "#8A5100",
    note: "Warm highlight.",
  },
  {
    name: "Background",
    hex: "#F8F9FC",
    on: "#191C1E",
    note: "App canvas (light).",
  },
  {
    name: "On surface",
    hex: "#191C1E",
    on: "#FFFFFF",
    note: "Primary text.",
  },
  {
    name: "Error",
    hex: "#BA1A1A",
    on: "#FFFFFF",
    note: "Errors / 'you owe'.",
  },
];

const SCREENSHOTS = [
  { src: screenshotDashboard, alt: "Dashboard" },
  { src: screenshotGroupDetail, alt: "Group detail" },
  { src: screenshotGroups, alt: "Groups list" },
  { src: screenshotExpenseDetail, alt: "Expense detail" },
  { src: screenshotProfile, alt: "Profile" },
];

const STORE_SHORT =
  "Track and split shared expenses with friends, roommates, and trips — no spreadsheet.";

const STORE_FULL = `Tailspend keeps shared spending clear and settled.

Create a group for a trip, household, or one-off event, add everyone involved, and log who paid as you go. Tailspend works out every balance — what you owe and what you're owed — across people, currencies, and recurring bills.

• Split a whole expense or divide individual items — equally, by exact amounts, percentages, or shares
• Multiple people can pay, and cross-currency spending stays clear in the group's currency
• Scan a receipt to prefill an expense automatically
• Plan ahead with recurring expenses
• Invite people when they're ready, or share a read-only view with anyone who just needs to follow along
• Sign in securely with Google or Apple — Tailspend never sees your password

Free to use. No bank connection required.`;

const KEYWORDS = [
  "expense splitting",
  "split bills",
  "shared expenses",
  "group expenses",
  "roommates",
  "trips",
  "IOU",
  "settle up",
  "who owes what",
  "receipts",
];

/** README bundled into the brand kit zip — same source values as the page. */
function brandReadme(): string {
  const palette = PALETTE.map((c) => `- ${c.name}: ${c.hex} — ${c.note}`).join(
    "\n",
  );
  return `# ${APP.name} brand kit

${APP.tagline}
${APP.subtitle}

## Files
- logo.svg — horizontal lockup (logomark + wordmark)
- logomark.svg — logomark only
- app-icon.png — 1024×1024 app/store icon

## Colours
${palette}

## App metadata
- App name: ${APP.name}
- Category: ${APP.category}
- Bundle ID / Package: ${APP.bundleId}
- App Store Connect app ID: ${APP.iosAppId}
- Website: ${APP.website}
- Privacy policy: ${APP.privacy}
- Support: ${APP.support}

## Store listing copy

### Short description (<= 80 chars)
${STORE_SHORT}

### Full description
${STORE_FULL}

### Keywords
${KEYWORDS.join(", ")}
`;
}

async function buildBrandKit(): Promise<Blob> {
  const assets = await Promise.all(
    Object.entries(brandKitAssets).map(
      async ([name, url]): Promise<ZipEntry> => {
        // `url` is the app's own bundled/fingerprinted asset URL, so this
        // fetch works on the web and under `file://` in the desktop app.
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${name}`);
        return { name, data: new Uint8Array(await res.arrayBuffer()) };
      },
    ),
  );
  const readme: ZipEntry = {
    name: "README.md",
    data: new TextEncoder().encode(brandReadme()),
  };
  return makeZip([...assets, readme]);
}

export function BrandPage(): ReactNode {
  const { status } = useAuth();
  const home = status === "authenticated" ? "/" : "/welcome";
  const [kitState, setKitState] = useState<"idle" | "building" | "error">(
    "idle",
  );

  async function downloadKit(): Promise<void> {
    setKitState("building");
    try {
      const zip = await buildBrandKit();
      downloadBlob(zip, "tailspend-brand-kit.zip");
      setKitState("idle");
    } catch {
      setKitState("error");
    }
  }

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <header className="border-b border-outline-variant/40 bg-surface-container-lowest">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to={home} className="flex items-center gap-3">
            <BrandMark />
            <span className="text-headline-md text-primary">Tailspend</span>
          </Link>
          {status === "authenticated" ? (
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="size-4" />
                Back to dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/welcome">
              <Button size="sm">Sign in</Button>
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-stack-lg px-6 py-10 sm:px-8">
        <div className="space-y-2">
          <p className="text-label-sm text-on-surface-variant">Brand & press</p>
          <h1 className="text-headline-lg">Tailspend brand kit</h1>
          <p className="text-body-md text-on-surface-variant">
            Everything needed to represent Tailspend — logo, colours, voice —
            and copy-paste-ready metadata for the App Store and Google Play.
          </p>
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={downloadKit}
              disabled={kitState === "building"}
              size="sm"
            >
              <Download className="size-4" />
              {kitState === "building"
                ? "Preparing…"
                : "Download brand kit (.zip)"}
            </Button>
            {kitState === "error" ? (
              <span className="text-body-sm text-error">
                Couldn't build the kit — try again.
              </span>
            ) : null}
          </div>
        </div>

        <Section title="Logo">
          <div className="flex flex-wrap items-center gap-8">
            {/* Horizontal lockup (logomark + wordmark). */}
            <img src={logoUrl} alt="Tailspend logo" className="h-12 w-auto" />
            {/* Logomark on its own (rounded corners are baked into the art). */}
            <img
              src={logomarkUrl}
              alt="Tailspend logomark"
              width={56}
              height={56}
            />
            {/* App icon (1024×1024, used on the stores; corners baked in). */}
            <img
              src={appIconUrl}
              alt="Tailspend app icon"
              width={56}
              height={56}
            />
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <a href={logoUrl} download="logo.svg">
              <Button variant="secondary" size="sm">
                Logo (SVG)
              </Button>
            </a>
            <a href={logomarkUrl} download="logomark.svg">
              <Button variant="secondary" size="sm">
                Logomark (SVG)
              </Button>
            </a>
            <a href={appIconUrl} download="app-icon.png">
              <Button variant="secondary" size="sm">
                App icon (1024×1024 PNG)
              </Button>
            </a>
          </div>
          <p className="text-body-sm text-on-surface-variant">
            The wordmark is “Tailspend” — one word, single capital T. Keep clear
            space around it and don't recolour the mark. The logomark is a white
            receipt on the brand purple gradient; place it on a light background
            and don't add a border or alter its colours.
          </p>
        </Section>

        <Section title="Colours" contentClassName="grid gap-3 sm:grid-cols-2">
          {PALETTE.map((c) => (
            <div
              key={c.name}
              className="flex items-center gap-3 rounded-md border border-outline-variant/50 p-3"
            >
              <span
                aria-hidden="true"
                className="grid size-12 shrink-0 place-items-center rounded-md text-body-md font-bold"
                style={{ backgroundColor: c.hex, color: c.on ?? "#FFFFFF" }}
              >
                Aa
              </span>
              <div className="min-w-0">
                <p className="text-body-md font-bold">{c.name}</p>
                <p className="font-mono text-label-md text-on-surface-variant">
                  {c.hex}
                </p>
                <p className="text-body-sm text-on-surface-variant">{c.note}</p>
              </div>
            </div>
          ))}
        </Section>

        <Section
          title="Voice"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Plain, calm, and practical — like a friend who's good with money,
            not a bank. Short sentences. No jargon, no hype. We say what the app
            does, not how “revolutionary” it is.
          </p>
          <p>
            Tagline: <strong className="text-on-surface">{APP.tagline}</strong>
          </p>
          <p>
            One-liner:{" "}
            <strong className="text-on-surface">{APP.subtitle}</strong>
          </p>
        </Section>

        <Section title="Screenshots" contentClassName="space-y-3">
          <p className="text-body-md text-on-surface-variant">
            Current in-app screens (also used on the marketing site). Capture
            store screenshots from a device/simulator at the required sizes.
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {SCREENSHOTS.map((s) => (
              <img
                key={s.src}
                src={s.src}
                alt={s.alt}
                className="h-72 w-auto shrink-0 rounded-sm border border-outline-variant/40"
              />
            ))}
          </div>
        </Section>

        <Section title="App metadata" contentClassName="space-y-1">
          <MetaRow label="App name" value={APP.name} />
          <MetaRow label="Category" value={APP.category} />
          <MetaRow
            label="Bundle ID (iOS) / Package (Android)"
            value={APP.bundleId}
          />
          <MetaRow label="App Store Connect app ID" value={APP.iosAppId} />
          <MetaRow label="Website / marketing URL" value={APP.website} />
          <MetaRow label="Privacy policy URL" value={APP.privacy} />
          <MetaRow label="Support contact" value={APP.support} />
        </Section>

        <Section title="Store copy" contentClassName="space-y-6">
          <p className="text-body-md text-on-surface-variant">
            The exact text for the App Store and Google Play listings — copy it
            straight in.
          </p>
          <CopyBlock
            label="Subtitle / short description (≤ 80 chars)"
            text={STORE_SHORT}
          />
          <CopyBlock label="Full description" text={STORE_FULL} />
          <div className="space-y-2">
            <p className="text-body-sm font-semibold text-on-surface-variant">
              Keywords
            </p>
            <ul className="flex flex-wrap gap-2">
              {KEYWORDS.map((keyword) => (
                <li
                  key={keyword}
                  className="rounded-full bg-primary-fixed px-3 py-1 text-label-md text-on-primary-fixed-variant"
                >
                  {keyword}
                </li>
              ))}
            </ul>
          </div>
        </Section>
      </main>
    </div>
  );
}

function MetaRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactNode {
  return (
    <div className="flex flex-col gap-0.5 border-b border-outline-variant/30 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <span className="text-label-md text-on-surface-variant">{label}</span>
      <span className="break-all font-mono text-body-sm text-on-surface">
        {value}
      </span>
    </div>
  );
}

function CopyBlock({
  label,
  text,
}: {
  label: string;
  text: string;
}): ReactNode {
  return (
    <div className="space-y-2">
      <p className="text-body-sm font-semibold text-on-surface-variant">
        {label}
      </p>
      <p className="whitespace-pre-wrap rounded-md bg-surface-container-low p-4 text-body-md text-on-surface">
        {text}
      </p>
    </div>
  );
}
